// Import all imports under the IdentityImports namespace.
import * as IdentityImports from "../identity/helpers/imports";

// Import all types under the IdentityTypes namespace.
import * as IdentityTypes from "../identity/helpers/types";

// Import all configurations under the IdentityConfigs namespace.
import { IdentityConfigs } from "../identity/helpers/configs";

// Import the custom Amplify UI identity theme and flow.
import AmplifyFlow from "../identity/pages/AmplifyFlow";
import { IdentityTheme } from "../identity/pages/AmplifyFlow";
import { formFields } from "../identity/pages/AmplifyFlow";

// Feature Flag Imports
import { FeatureFlagProvider } from "featureflag/FeatureFlagProvider";

import { SQW_Log, DebugLevel } from "services/Logging";

IdentityImports.Hub.listen("auth", ({ payload }) => {
    switch (payload.event) {
        case "signedIn":
            SQW_Log(DebugLevel.LOG, "user have been signedIn successfully.");
            break;
        case "signedOut":
            SQW_Log(DebugLevel.LOG, "user have been signedOut successfully.");
            break;
        case "tokenRefresh":
            SQW_Log(DebugLevel.LOG, "auth tokens have been refreshed.");
            break;
        case "tokenRefresh_failure":
            SQW_Log(DebugLevel.LOG, "failure while refreshing auth tokens.");
            break;
        case "signInWithRedirect":
            SQW_Log(DebugLevel.LOG, "signInWithRedirect API has successfully been resolved.");
            break;
        case "signInWithRedirect_failure":
            SQW_Log(DebugLevel.LOG, "failure while trying to resolve signInWithRedirect API.");
            break;
        case "customOAuthState":
            SQW_Log(DebugLevel.LOG, "custom state returned from CognitoHosted UI");
            break;
    }
});

const getDomain = () => {
    const host = window.location.host.split(":")[0];
    if (host.indexOf("localhost") >= 0) return "localhost";
    const spltAddr = host.split(".");
    if (spltAddr && spltAddr.length > 2) {
        return spltAddr[1] + "." + spltAddr[2];
    }

    return host;
};

const getCookieStorage = () => {
    let cs = new IdentityImports.CookieStorage();

    cs.domain = getDomain();
    cs.path = "/";
    cs.expires = 365;
    cs.sameSite = "strict";
    cs.secure = window.location.protocol === "https:";

    SQW_Log(DebugLevel.LOG, "Window Protocol: ", window.location.protocol);
    SQW_Log(DebugLevel.LOG, "CookieStorage: ", cs);

    return cs;
};

function converterRead(value: string, name: string) {
    if (value[0] === '"') {
        value = value.slice(1, -1);
    }
    return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
}

function ClearAllCookies() {
    if (typeof document === "undefined") return;

    const cookies = document.cookie.split("; ");
    for (let cookie of cookies) {
        const cookieName = cookie.split("=")[0];
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        SQW_Log(DebugLevel.LOG, "Clearing Cookie: ", cookieName);
    }
}

IdentityImports.Amplify.configure(IdentityConfigs.amplifyConfig);

IdentityImports.cognitoUserPoolsTokenProvider.setKeyValueStorage(getCookieStorage());

type AppProps = {
    signOut?: IdentityTypes.UseAuthenticator["signOut"]; //() => void;
    user?: IdentityTypes.AuthUser;
};

const Authenticate: React.FC<AppProps> = ({ signOut, user }) => {
    const [state, setState] = IdentityImports.useState({
        logo: "https://prism-web-images-prod.s3.us-east-1.amazonaws.com/logos/advantage.webp",
        sideImage: "https://prism-web-images-prod.s3.us-east-1.amazonaws.com/login_images/advantage.jpg",
        loading: false,
        tenant: {} as IdentityTypes.GetTenantsQuery,
        invalidAccountNum: false,
        userTenants: {} as IdentityTypes.TenantsUsersByUsersIdQuery,
        needSelectTenant: false,
        authComponents: {},
        selectedTenantName: "",
        hideSignup: true,
        username: "",
        isAuthenticated: false,
        userNameExists: false,
        currentTenantName: "",
        currentLocation: "",
    });

    const {
        logo,
        sideImage,
        loading,
        tenant,
        invalidAccountNum,
        userTenants,
        needSelectTenant,
        authComponents,
        selectedTenantName,
        hideSignup,
        username,
        isAuthenticated,
        userNameExists,
        currentTenantName,
        currentLocation,
    } = state; // Destructure state for easier access.

    const [rememberMe, setRememberMe] = IdentityImports.useState<boolean>(false);

    const { tokens } = IdentityImports.useTheme();

    const location = window.location.host;

    if (location != currentLocation) {
        setState((prevState) => ({
            ...prevState,
            currentLocation: location,
        }));

        CallGetTenantName();

        SQW_Log(DebugLevel.LOG, "t1: ", currentTenantName);
    }

    const services = {
        async handleSignIn(formData: IdentityTypes.SignInInput) {
            let selTenant = "";

            const subdomain = window.location.host.split(".")[0];
            SQW_Log(DebugLevel.LOG, "Sign in subdomain: ", subdomain);
            const domainTenant = await IdentityImports.GetTenantInfoFromName(subdomain);
            if (!domainTenant?.id) {
                SQW_Log(DebugLevel.LOG, "Sign in: No tenant found for subdomain: ", subdomain);
                if (!selectedTenantName || selectedTenantName === "") {
                    SQW_Log(DebugLevel.LOG, 
                        "Sign in: No selected tenant found.  Getting from local storage...",
                    );
                    const selTnt = await localStorage.getItem("selectedTenant");
                    SQW_Log(DebugLevel.LOG, "localstore: ", selTnt);
                    let tenant: any = undefined;
                    if (selTnt && selTnt !== "") {
                        const tmpTenant = await IdentityImports.GetTenantInfoFromName(selTnt);
                        if (tmpTenant?.id) {
                            setState((prevState) => ({
                                ...prevState,
                                selectedTenantName: tmpTenant.id,
                            }));

                            selTenant = tmpTenant.id;
                        }
                    }
                } else {
                    SQW_Log(DebugLevel.LOG, "Sign in: Selected tenant: ", selectedTenantName);
                    selTenant = selectedTenantName;
                }
                SQW_Log(DebugLevel.LOG, needSelectTenant, selTenant);
                if (needSelectTenant === false && !window.location.host.startsWith(selTenant)) {
                    setState((prevState) => ({
                        ...prevState,
                        selectedTenantName: "",
                    }));

                    selTenant = "";
                }
            } else {
                selTenant = subdomain;
            }

            SQW_Log(DebugLevel.LOG, "Handle Sign in....", formData);
            SQW_Log(DebugLevel.LOG, "SELECTED TENANT local: ", selTenant);
            SQW_Log(DebugLevel.LOG, "SELECTED TENANT State Name: ", selectedTenantName);
            SQW_Log(DebugLevel.LOG, "local selected tenant", selectedTenant);
            try {
                SQW_Log(DebugLevel.LOG, "selecting user tenants...");
                const userTenants = await IdentityImports.SelectUserTenants(formData.username);
                SQW_Log(DebugLevel.LOG, "done selecting user tenants...");
                if (
                    (!selTenant || selTenant === "") &&
                    userTenants?.data?.tenantsUsersByUsersId?.items
                ) {
                    SQW_Log(DebugLevel.LOG, 
                        "Need Select Tenant: ",
                        userTenants.data.tenantsUsersByUsersId.items,
                    );
                    setState((prevState) => ({
                        ...prevState,
                        userTenants: userTenants.data,
                        needSelectTenant: true,
                        username: formData.username,
                    }));

                    SQW_Log(DebugLevel.LOG, "Calling signIn...");
                    var ret = await IdentityImports.signIn(formData);
                    SQW_Log(DebugLevel.LOG, "signIn returned: ", ret);
                    return ret;
                } else {
                    SQW_Log(DebugLevel.LOG, 
                        "Do not Need Select Tenant: ",
                        userTenants.data.tenantsUsersByUsersId.items,
                    );
                    setState((prevState) => ({
                        ...prevState,
                        userTenants: {},
                        needSelectTenant: false,
                    }));
                }

                SQW_Log(DebugLevel.LOG, formData.username + " tenants: ", userTenants);

                const userTenantRecord = userTenants?.data?.tenantsUsersByUsersId?.items?.find(
                    (ut) => ut.tenantsId === selTenant,
                );
                if (!userTenantRecord) throw new Error("You don't have access to this tenant.");

                if (!window.location.host.startsWith(selTenant))
                    IdentityImports.GotoTenantURL(selTenant);
            } catch (e) {
                SQW_Log(DebugLevel.LOG, "Could not get user tenants: ", e);
            }

            if (rememberMe) {
                IdentityImports.rememberDevice()
                    .then(() => {
                        SQW_Log(DebugLevel.LOG, "Remembered device.");
                    })
                    .catch((err) => {
                        SQW_Log(DebugLevel.LOG, "Error remembering device: ", err);
                    });
            }
            SQW_Log(DebugLevel.LOG, "456: Setting user: ", formData.username);
            localStorage.setItem("user", formData.username);

            var ret = await IdentityImports.signIn(formData);
            SQW_Log(DebugLevel.LOG, "signIn returned: ", ret);
            return ret;
        },

        async validateCustomSignUp(formData: any) {},

        async handleSignUp(formData: IdentityTypes.SignUpInput) {
            const fname = formData.options?.userAttributes?.given_name || "";
            const lname = formData.options?.userAttributes?.family_name || "";
            var accNum = formData.options?.userAttributes?.nickname || "";

            SQW_Log(DebugLevel.LOG, "334 1", fname);
            SQW_Log(DebugLevel.LOG, "334 2", lname);
            SQW_Log(DebugLevel.LOG, "334 3", accNum);

            let { username, password, options } = formData;
            let selTenant = "";

            if (username === "" || password === "" || accNum === "" || fname === "" || lname === "")
                throw new Error("You must fill in all of the fields.");

            username = username.toLowerCase();
            SQW_Log(DebugLevel.LOG, "username: ", username);
            SQW_Log(DebugLevel.LOG, "Options: ", options);
            SQW_Log(DebugLevel.LOG, "Validation Data", options?.validationData);
            SQW_Log(DebugLevel.LOG, "AccountNum: ", accNum);

            const subdomain = window.location.host.split(".")[0];
            SQW_Log(DebugLevel.LOG, "Sign in subdomain: ", subdomain);
            const domainTenant = await IdentityImports.GetTenantInfoFromName(subdomain);
            if (!domainTenant?.id) {
                throw new Error("Invalid subdomain, cannot sign up.");
            } else {
                selTenant = subdomain;
            }

            await IdentityImports.fetchSecrets();
            const token = await IdentityImports.API_Authenticate();
            const bypass = accNum.startsWith("PrsmOvrd:");
            if (bypass) accNum = accNum.replace("PrsmOvrd:", "");
            const regNewUser = await IdentityImports.API_RegisterNewUser(
                token,
                accNum,
                username,
                bypass,
            );

            if (!regNewUser) throw new Error("Error registering new user.");

            SQW_Log(DebugLevel.LOG, "aaaaASDFASDFASDFASDFASDFASDFASDFASDFASDFASDF", regNewUser);
            if (!regNewUser.IsSuccessful)
                throw new Error(
                    "Error registering new user.  " +
                        regNewUser.ErrorCode +
                        ": " +
                        regNewUser.Error,
                );

            try {
                const ret = await IdentityImports.signUp({
                    username,
                    password,
                    options,
                });
                SQW_Log(DebugLevel.LOG, "Sign Up return: ", ret);
                if (ret.isSignUpComplete || ret.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
                    registerUserWithCustomerNumber(formData.username, selTenant, fname, lname);
                }

                return ret;
            } catch (err: any) {
                SQW_Log(DebugLevel.LOG, "Error message: ", err.message);
                if (err.name === "UsernameExistsException") {
                    setState((prevState) => ({
                        ...prevState,
                        userNameExists: true,
                    }));
                    registerUserWithCustomerNumber(formData.username, selTenant, fname, lname);
                    SQW_Log(DebugLevel.LOG, "User Name already exists.");
                    throw new Error(
                        "User Name already exists, but customer has now been linked to user.  Please sign in using your existing password, or choose forgot password if you have forgotten your password.",
                    );
                }

                SQW_Log(DebugLevel.LOG, "Sign Up error: ", err);
                throw err;
            }
        },
    };

    async function registerUserWithCustomerNumber(
        username: string,
        selTenant: string,
        firstname: string,
        lastname: string,
    ) {
        SQW_Log(DebugLevel.LOG, "t1");
        const allTenantsUsers = await IdentityImports.SelectAllTenantsUsers();
        SQW_Log(DebugLevel.LOG, "All tenants users: ", allTenantsUsers);
        const userTenants = await IdentityImports.SelectUserTenants(username);
        SQW_Log(DebugLevel.LOG, username + " tenants: ", userTenants);
        const userTenantRecord = userTenants?.data?.tenantsUsersByUsersId?.items?.find(
            (ut) => ut.tenantsId === selTenant,
        );
        SQW_Log(DebugLevel.LOG, "User Tenant Record: ", userTenantRecord);
        if (!userTenantRecord || userTenantRecord.usersId !== username.trim().toLowerCase()) {
            SQW_Log(DebugLevel.LOG, "Inserting user", username, firstname, lastname, selTenant);
            const res = await IdentityImports.InsertUser(selTenant, username, firstname, lastname);
            if (!res) SQW_Log(DebugLevel.LOG, "Insert user returned nothing.");
            else {
                SQW_Log(DebugLevel.LOG, "Insert user result:", res);
            }
        }
        SQW_Log(DebugLevel.LOG, "t2");
    }

    const [authServices, setAuthServices] = IdentityImports.useState(services);

    const setSelectedTenant = async (tenantId: string) => {
        localStorage.removeItem("selectedTenant");
        if (tenantId) {
            SQW_Log(DebugLevel.LOG, "Getting selected tenant info for " + tenantId);
            const tenantObj = await IdentityImports.GetTenantInfoFromName(tenantId);
            if (tenantObj) {
                SQW_Log(DebugLevel.LOG, "Got selected tenant info: Setting tenant...", tenantObj);
                setState((prevState) => ({
                    ...prevState,
                    tenant: { ...prevState.tenant, ...tenantObj, selectedTenant: tenantObj },
                    selectedTenantName: tenantId,
                }));
                localStorage.setItem("selectedTenant", tenantId);
            }
        }
    };
    const handleTenantChange = (event: IdentityTypes.SelectChangeEvent) => {
        SQW_Log(DebugLevel.LOG, "Selected Tenant changed. ", event.target);
        setSelectedTenant(event.target.value);
    };

    IdentityImports.useEffect(() => {
        if (!currentTenantName && currentTenantName !== "" && !loadingTenantName)
            getTenantNameWithWait();
    });

    const getTenantNameWithWait = () => {
        setLoadingTenantName(true);
        for (let i = 0; i < 10; i++) {
            if (currentTenantName !== "") {
                break;
            }
            (async () => await new Promise((resolve) => setTimeout(resolve, 500)))();
        }
        setLoadingTenantName(false);
        return currentTenantName;
    };

    const components = {
        Header: () => (
            <AmplifyFlow.Header
                logo={state.logo}
                companyName={IdentityImports.GetCompanyName()}
            />
        ),
        SignIn: {
            Header: AmplifyFlow.SignIn.Header,
            Footer: () => (
                <AmplifyFlow.SignIn.Footer
                    rememberMe={rememberMe}
                    setRememberMe={setRememberMe}
                />
            ),
        },
        SignUp: {
            Header: AmplifyFlow.SignUp.Header,
            FormFields: AmplifyFlow.SignUp.FormFields,
            Footer: AmplifyFlow.SignUp.Footer,
        },
        ForgotPassword: {
            Header: AmplifyFlow.ForgotPassword.Header,
            Footer: AmplifyFlow.ForgotPassword.Footer,
        },
        Footer: AmplifyFlow.Footer,
    };

    IdentityImports.useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            authComponents: components,
            authServices: services,
        }));
    }, [needSelectTenant]);

    let selectedTenant: any = null;
    IdentityImports.useEffect(() => {
        SQW_Log(DebugLevel.LOG, "Tenant useEffect: ", tenant);
        selectedTenant = tenant;
    }, [tenant]);

    IdentityImports.useEffect(() => {
        SQW_Log(DebugLevel.LOG, "Loading authenticate...");
        setState((prevState) => ({
            ...prevState,
            loading: true,
        }));

        try {
            IdentityImports.setAWSTokens();
            getTenantData();
        } catch (err) {
            SQW_Log(DebugLevel.LOG, "Authenticate: useEffect[] error: ", err);
        } finally {
            setState((prevState) => ({
                ...prevState,
                loading: false,
            }));
        }
    }, []);

    IdentityImports.useEffect(() => {
        if (loading) {
            setState((prevState) => ({
                ...prevState,
                authComponents: components,
                authServices: services,
            }));

            // setState is asynchronous. Call getTenantData in the next event loop tick to ensure it executes after the state updates.
            setTimeout(() => {
                getTenantData();
            }, 0);
        }
    }, [selectedTenantName, loading, components, services]);

    IdentityImports.useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            authComponents: components,
        }));
    }, [logo]);

    const getTenantData = async () => {
        SQW_Log(DebugLevel.LOG, "Getting tenant data...  Current tenant: ", tenant);

        if (!selectedTenantName || selectedTenantName === "") {
            const tenantObj = await IdentityImports.GetTenantInfo();
            if (tenantObj) {
                SQW_Log(DebugLevel.LOG, "UseEffect... Setting tenant...", tenantObj);
                setState((prevState) => ({
                    ...prevState,
                    currentTenantName: tenantObj.id,
                    selectedTenant: tenantObj.id,
                    selectedTenantName: tenantObj.id,
                    hideSignup: false,
                }));
            }
        }
        const updateImages = async () => {
            const logo = await IdentityImports.GetCompanyLogo();
            const sideImage = await IdentityImports.GetCompanySideImage();
            setState((prevState) => ({
                ...prevState,
                logo: logo,
                sideImage: sideImage,
            }));
        };

        updateImages();
    };

    function onChooseTenant(tenantName: string): void {
        setSelectedTenant(tenantName);
        localStorage.setItem("selectedTenant", tenantName);
        SQW_Log(DebugLevel.LOG, "onChooseTenant: ", tenantName);
        IdentityImports.GotoTenantURL(tenantName);
    }

    const SignedIn = () => {
        setState((prevState) => ({
            ...prevState,
            selectedTenantName: "",
        }));

        return true;
    };

    const signOutUser = async () => {
        localStorage.removeItem("selectedTenant");

        setState((prevState) => ({
            ...prevState,
            selectedTenantName: "",
        }));

        signOut && signOut({ global: true });

        ClearAllCookies();
    };

    async function CallGetTenantName() {
        if ((!currentTenantName || currentTenantName === "") && !loading) {
            setState((prevState) => ({
                ...prevState,
                loading: true,
            }));
        }

        try {
            const tenantObj = await IdentityImports.GetTenantInfo();
            if (tenantObj) {
                setState((prevState) => ({
                    ...prevState,
                    currentTenantName: tenantObj.id,
                }));
            } else {
                SQW_Log(DebugLevel.LOG, "Authenticate: CallGetTenantName: No Tenant Found");
                setState((prevState) => ({ ...prevState, loading: false }));
            }
        } catch (err) {
            SQW_Log(DebugLevel.LOG, "Authenticate: CallGetTenantName: Error: ", err);
            setState((prevState) => ({ ...prevState, loading: false }));
        }
    }

    IdentityImports.useEffect(() => {
        CallGetTenantName();
        SQW_Log(DebugLevel.LOG, "Location Changed: ", location);
    }, [location]);
    const [loadingTenantName, setLoadingTenantName] = IdentityImports.useState(false);

    let aStatus = "";
    let auth: any = null;
    try {
        const { authStatus } = IdentityImports.useAuthenticator((context) => [context.authStatus]);
        aStatus = authStatus;
        auth = IdentityImports.useAuthenticator((context) => [context.user]);
        user = auth.user;
        signOut = auth.signOut;
    } catch (err) {
        SQW_Log(DebugLevel.LOG, "Authenticate: useAuthenticator error: ", err);
    }

    SQW_Log(DebugLevel.LOG, "authStatus: ", aStatus);
    SQW_Log(DebugLevel.LOG, "user: ", user);
    SQW_Log(DebugLevel.LOG, "444 TenantName: ", currentTenantName);

    return (
        <>
            {loading ?
                <IdentityImports.LoadingSpinner message='Loading...' />
            :   <IdentityImports.Stack
                    width={"100vw"}
                    height={"100vh"}
                    direction='row'
                    spacing={0}
                    justifyContent={"space-between"}
                >
                    {aStatus === "authenticated" && !loadingTenantName ? null : (
                        <IdentityImports.Hidden smDown>
                            <IdentityImports.View height='100vh'>
                                <IdentityImports.Image
                                    alt='Image'
                                    src={sideImage}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                        if (sideImage !== "")
                                            setState((prevState) => ({
                                                ...prevState,
                                                sideImage: sideImage,
                                            }));
                                    }}
                                    maxWidth='63vw'
                                    maxHeight='100vh'
                                />
                            </IdentityImports.View>
                        </IdentityImports.Hidden>
                    )}
                    <IdentityImports.Box
                        width='100%'
                        sx={{ backgroundColor: "transparent" }}
                        justifyContent='center'
                    >
                        <IdentityImports.ThemeProvider theme={IdentityTheme}>
                            <IdentityImports.Authenticator
                                components={components}
                                services={services}
                                formFields={formFields}
                                hideSignUp={hideSignup}
                            >
                                {({ signOut, user }) =>
                                    aStatus !== "authenticated" ? <div>Loading...</div>
                                    : currentTenantName && currentTenantName != "" ?
                                        <FeatureFlagProvider>
                                            <IdentityImports.App
                                                signout={signOutUser}
                                                user={user}
                                            />
                                        </FeatureFlagProvider>
                                    : !loadingTenantName ?
                                        <IdentityImports.Delayed waitBeforeShow={2000}>
                                            <IdentityImports.TenantChooser
                                                username={
                                                    auth?.user?.signInDetails?.loginId ?
                                                        auth?.user?.signInDetails?.loginId
                                                    :   IdentityImports.GetUserName()
                                                }
                                                onChooseTenant={onChooseTenant}
                                                onSignOut={() => signOutUser()}
                                            />
                                        </IdentityImports.Delayed>
                                    :   <></>
                                }
                            </IdentityImports.Authenticator>
                        </IdentityImports.ThemeProvider>
                    </IdentityImports.Box>
                </IdentityImports.Stack>
            }
        </>
    );
};

export default Authenticate;
